import { useState, useEffect, useMemo, useRef } from "react";
import { Big } from "big.js";
import {
  DRAG_START_REQUEST,
  ON_OVERLAY_DRAG,
  END_OVERLAY_DRAG,
} from "../constants/DragOverlayConstants";
import { isSafari } from "@/constants/BrowserConstants";

/**
 * @typedef {import("../types/DragOverlayTypes.mjs").DragOverlayEvent} DragOverlayEvent
 * @typedef {import("../types/DragOverlayTypes.mjs").DragOverlaySubscriberCallback} DragOverlaySubscriberCallback
 */

const LAST_DRAG_RESET = { x: 0, y: 0 };

/**
 *
 * @param {string} id
 * @param {PointerEvent} event
 * @emits DragOverlayEvent
 */
export const emitDragStart = (id, event) => {
  document.dispatchEvent(
    new CustomEvent(DRAG_START_REQUEST, {
      detail: { x: event.clientX, y: event.clientY, id },
    }),
  );
};

/**
 *
 * @param {string} id - identifies drag instance.
 * @param {DragOverlaySubscriberCallback} onDrag
 * @param {(() => void)=} onDragEnd
 * @returns
 */
const useOverlayDrag = (id, onDrag, onDragEnd) => {
  const [unsubscribers, setUnsubscribers] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [shouldUnsubscribe, setShouldUnsubscribe] = useState(false);
  const lastDrag = useRef({ ...LAST_DRAG_RESET });

  // the []-dep unmount cleanup below would otherwise close over the first
  // render's empty array and leak the document listeners when unmounting
  // mid-drag
  const unsubscribersRef = useRef(unsubscribers);
  unsubscribersRef.current = unsubscribers;

  /**
   * Hand this to a pointer/touch/mouse down callback, or generate an event and hand it to this.
   * @param {PointerEvent} event
   */
  const startDrag = (event) => {
    // Touch pointers are implicitly captured by the element first touched,
    // which would keep every move on the divider and away from the
    // full-screen overlay; the overlay would then never see pointerup and
    // never reset. Mouse pointers have no implicit capture, so this is a
    // no-op for them and desktop is unaffected.
    if (
      event?.pointerId !== undefined &&
      event.target?.hasPointerCapture?.(event.pointerId)
    ) {
      event.target.releasePointerCapture(event.pointerId);
    }
    emitDragStart(id, event);
    lastDrag.current = { ...LAST_DRAG_RESET };
    setDragging(true);
  };

  /**
   * Handles drag overlay events and applies them when appropriate
   * @param {DragOverlayEvent} event
   */
  const handleDragUpdate = (event) => {
    if (dragging && id === event?.detail?.id) {
      const { id, x, y } = event.detail;
      let { movementX, movementY } = event.detail;

      /**
       * Accounts for newer safari more or less clamping all movements to 1 (likely bug)
       * Handled here rather than the overlay in case other functionality depends on broken behavior
       */
      if (isSafari) {
        movementX = new Big(x).minus(lastDrag.current.x).toNumber();
        movementY = new Big(y).minus(lastDrag.current.y).toNumber();
        lastDrag.current.x = x;
        lastDrag.current.y = y;
      }

      onDrag && onDrag({ id, x, y, movementX, movementY });
    }
  };

  /**
   * Ends drag cycle and resets
   * @param {DragOverlayEvent} event
   */
  const handleDragEnd = (event) => {
    if (dragging && event.detail?.id === id) {
      setDragging(false);
      setShouldUnsubscribe(true);
      onDragEnd && onDragEnd();
    }
  };

  const addListeners = () => {
    const dragCB = (event) => handleDragUpdate(event);
    const endCB = (event) => handleDragEnd(event);

    document.addEventListener(ON_OVERLAY_DRAG, dragCB);
    document.addEventListener(END_OVERLAY_DRAG, endCB);

    const callbacks = [
      () => document.removeEventListener(ON_OVERLAY_DRAG, dragCB),
      () => document.removeEventListener(END_OVERLAY_DRAG, endCB),
    ];

    setUnsubscribers(callbacks);
  };

  const unsubscribe = () => {
    unsubscribers.forEach((unsubscribe) => unsubscribe());
    setUnsubscribers([]);
  };

  useEffect(() => {
    if (dragging && !unsubscribers.length) {
      addListeners();
    }
  }, [dragging, onDrag]);

  useEffect(() => {
    if (shouldUnsubscribe) {
      unsubscribe();
      setShouldUnsubscribe(false);
    }
  }, [shouldUnsubscribe, unsubscribers]);

  useEffect(() => {
    return () =>
      unsubscribersRef.current.forEach((cb) => {
        cb();
      });
  }, []);

  return useMemo(() => [startDrag], [onDrag]);
};

export default useOverlayDrag;
